import React, {
  useState,
  useEffect,
} from "react";
import {
  Heart,
  Calendar,
  Baby,
  Shield,
  // AlertTriangle,
  // User,
  // Phone,
  // MapPin,
  // FileText,
  Clock,
  Stethoscope,
} from "lucide-react";

import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  required?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

/* eslint-disable @typescript-eslint/no-empty-object-type */
interface GynecologyFormStepProps {
  // Add properties here if needed
}
/* eslint-enable @typescript-eslint/no-empty-object-type */

const InputField: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  icon: Icon,
  required = false,
  inputProps = {},
}) => (
  <div className="space-y-2">
    <label className="flex items-center text-sm font-semibold text-gray-700">
      {Icon && <Icon className="mr-2 text-pink-500" width={16} height={16} />}
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 bg-white/80 backdrop-blur-sm ${inputProps.disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        {...inputProps}
      />
    </div>
  </div>
);

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex items-center space-x-3 p-4 rounded-xl border-2 bg-gradient-to-br from-pink-50 to-rose-50 border-pink-100 cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
    <div className="relative">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div
        className={`w-5 h-5 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${checked
          ? "bg-gradient-to-br from-pink-500 to-pink-600 border-pink-600"
          : "border-gray-300 bg-white"
          }`}
      >
        {checked && (
          <svg
            className="w-3 h-3 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
    </div>
    <span className="text-sm font-medium text-gray-700">{label}</span>
  </label>
);

const GynecologyFormStep: React.FC<GynecologyFormStepProps> = () => {
  const { formData, updateFormData } = useConsultationWizardState();
  const [currentDate] = useState(new Date().toISOString().split("T")[0]);
  const [showPregnancyForm, setShowPregnancyForm] = useState(
    formData.informationGynecologique?.grossesseActuelle !== undefined
  );

  useEffect(() => {
    if (!formData.patientInfo.dateVisite) {
      updateFormData("patientInfo", "dateVisite", currentDate);
    }
  }, [currentDate, updateFormData, formData.patientInfo.dateVisite]);

  // ❌ DUPLIQUÉ avec PatientInfoStep - Commenté car les infos patient sont déjà saisies dans PatientInfoStep
  // const updatePatientInfo = (field: string, value: string) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     patientInfo: {
  //       ...prev.patientInfo,
  //       [field]: value,
  //     },
  //   }));
  // };

  const updateGynecologyInfo = (field: string, value: string | boolean | object | undefined) => {
    const currentInfo = formData.informationGynecologique || {
      ageMenopause: "",
      dateHysterectomie: "",
      dernierFrottis: "",
      frottisAnormal: false,
      derniereMammographie: "",
      nombreGrossesses: "",
      methodeContraceptiveActuelle: "",
      methodesContraceptivesPassees: "",
      dateDernierRegle: "",
      cycleTousLesJours: "",
      nombreEnfants: "",
      nombreEnfantsVivants: "",
      nombreEnfantsDecedes: "",
      nombreMortNes: "",
      nombreFausseCouches: "",
      typeAccouchement: "",
    };

    if (field === 'grossesseActuelle' && value === undefined) {
      const { grossesseActuelle: _, ...rest } = currentInfo;
      updateFormData("informationGynecologique", rest);
    } else {
      updateFormData("informationGynecologique", {
        ...currentInfo,
        [field]: value,
      });
    }
  };

  const updatePregnancyInfo = (field: string, value: string | boolean) => {
    const currentInfo = formData.informationGynecologique || {
      ageMenopause: "",
      dateHysterectomie: "",
      dernierFrottis: "",
      frottisAnormal: false,
      derniereMammographie: "",
      nombreGrossesses: "",
      methodeContraceptiveActuelle: "",
      methodesContraceptivesPassees: "",
      dateDernierRegle: "",
      cycleTousLesJours: "",
      nombreEnfants: "",
      nombreEnfantsVivants: "",
      nombreEnfantsDecedes: "",
      nombreMortNes: "",
      nombreFausseCouches: "",
      typeAccouchement: "",
    };

    const currentPregnancy = currentInfo.grossesseActuelle || {
      dateDebutGrossesse: currentDate,
      dateAccouchementPrevu: "",
      grossesseDesiree: false,
      grossesseMultiple: false,
      nombreFoetus: "1",
      groupeSanguin: "",
      rh: "",
      rci: false,
      menaceAccouchementPremature: false,
      diabeteGestationnel: false,
      hypertensionArterielle: false,
      preeclampsie: false,
      autresPathologies: "",
      traitementEnCours: "",
      allergies: "",
      antecedentsObstetricaux: "",
      commentaires: "",
    };

    updateFormData("informationGynecologique", {
      ...currentInfo,
      grossesseActuelle: {
        ...currentPregnancy,
        [field]: value,
      },
    });
  };

  const togglePregnancyForm = () => {
    if (showPregnancyForm) {
      const { grossesseActuelle: _, ...rest } = formData.informationGynecologique || {};
      const info = rest as {
        ageMenopause?: string;
        dateHysterectomie?: string;
        dernierFrottis?: string;
        frottisAnormal?: boolean;
        derniereMammographie?: string;
        nombreGrossesses?: string;
        methodeContraceptiveActuelle?: string;
        methodesContraceptivesPassees?: string;
        dateDernierRegle?: string;
        cycleTousLesJours?: string;
        nombreEnfants?: string;
        nombreEnfantsVivants?: string;
        nombreEnfantsDecedes?: string;
        nombreMortNes?: string;
        nombreFausseCouches?: string;
        typeAccouchement?: string;
      };

      updateFormData("informationGynecologique", {
        ageMenopause: info.ageMenopause || "",
        dateHysterectomie: info.dateHysterectomie || "",
        dernierFrottis: info.dernierFrottis || "",
        frottisAnormal: info.frottisAnormal || false,
        derniereMammographie: info.derniereMammographie || "",
        nombreGrossesses: info.nombreGrossesses || "",
        methodeContraceptiveActuelle: info.methodeContraceptiveActuelle || "",
        methodesContraceptivesPassees: info.methodesContraceptivesPassees || "",
        dateDernierRegle: info.dateDernierRegle || "",
        cycleTousLesJours: info.cycleTousLesJours || "",
        nombreEnfants: info.nombreEnfants || "",
        nombreEnfantsVivants: info.nombreEnfantsVivants || "",
        nombreEnfantsDecedes: info.nombreEnfantsDecedes || "",
        nombreMortNes: info.nombreMortNes || "",
        nombreFausseCouches: info.nombreFausseCouches || "",
        typeAccouchement: info.typeAccouchement || "",
      });
    } else {
      updatePregnancyInfo('dateDebutGrossesse', currentDate);
    }
    setShowPregnancyForm(!showPregnancyForm);
  };

  // const calculateAge = (dateNaissance: string) => {
  //   if (dateNaissance) {
  //     const today = new Date();
  //     const birthDate = new Date(dateNaissance);
  //     let age = today.getFullYear() - birthDate.getFullYear();
  //     const monthDiff = today.getMonth() - birthDate.getMonth();
  //     if (
  //       monthDiff < 0 ||
  //       (monthDiff === 0 && today.getDate() < birthDate.getDate())
  //     ) {
  //       age--;
  //     }
  //     updatePatientInfo("age", age.toString());
  //   }
  // };

  return (
    <div className="space-y-8">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => {
          const icons = [Heart, Baby, Shield, Stethoscope];
          const Icon = icons[i % icons.length];
          const colors = [
            "text-pink-200",
            "text-rose-200",
            "text-purple-200",
            "text-fuchsia-200",
          ];

          return (
            <div
              key={i}
              className={`absolute ${colors[i % colors.length]
                } opacity-20 animate-float`}
            >
              <Icon className="w-5 h-5" />
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-br from-pink-50 via-white to-rose-50 rounded-3xl p-6 sm:p-8 shadow-2xl border border-pink-100 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-400 via-rose-400 to-purple-400 rounded-t-3xl"></div>

        <div className="flex items-center mb-8">
          <div className="bg-gradient-to-br from-pink-100 to-rose-100 p-4 rounded-2xl mr-4">
            <Heart className="text-pink-600" size={32} />
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
              Formulaire Gynécologie
            </h2>
            <p className="text-gray-600 font-medium">
              Consultation gynécologique complète
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 sm:p-6 rounded-2xl shadow-lg border border-purple-100 mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <Heart className="mr-2 text-purple-600" size={24} />
            Historique Gynécologique
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InputField
              label="Âge de la ménopause"
              value={formData.informationGynecologique?.ageMenopause || ""}
              onChange={(value) => updateGynecologyInfo("ageMenopause", value)}
              type="number"
              icon={Calendar}
            />

            <InputField
              label="Date de l'hystérectomie"
              value={formData.informationGynecologique?.dateHysterectomie || ""}
              onChange={(value) =>
                updateGynecologyInfo("dateHysterectomie", value)
              }
              type="date"
              icon={Calendar}
            />

            <InputField
              label="Dernier frottis"
              value={formData.informationGynecologique?.dernierFrottis || ""}
              onChange={(value) =>
                updateGynecologyInfo("dernierFrottis", value)
              }
              type="date"
              icon={Calendar}
            />

            <CheckboxField
              label="Frottis anormal"
              checked={
                formData.informationGynecologique?.frottisAnormal || false
              }
              onChange={(checked) =>
                updateGynecologyInfo("frottisAnormal", checked)
              }
            />

            <InputField
              label="Dernière mammographie"
              value={
                formData.informationGynecologique?.derniereMammographie || ""
              }
              onChange={(value) =>
                updateGynecologyInfo("derniereMammographie", value)
              }
              type="date"
              icon={Calendar}
            />

            <InputField
              label="Nombre de grossesses"
              value={formData.informationGynecologique?.nombreGrossesses || ""}
              onChange={(value) =>
                updateGynecologyInfo("nombreGrossesses", value)
              }
              type="number"
              icon={Baby}
            />

            <InputField
              label="Méthode contraceptive actuelle"
              value={
                formData.informationGynecologique
                  ?.methodeContraceptiveActuelle || ""
              }
              onChange={(value) =>
                updateGynecologyInfo("methodeContraceptiveActuelle", value)
              }
              icon={Shield}
            />

            <InputField
              label="Méthodes contraceptives passées"
              value={
                formData.informationGynecologique
                  ?.methodesContraceptivesPassees || ""
              }
              onChange={(value) =>
                updateGynecologyInfo("methodesContraceptivesPassees", value)
              }
              icon={Shield}
            />

            <InputField
              label="Date des dernières règles"
              value={formData.informationGynecologique?.dateDernierRegle || ""}
              onChange={(value) =>
                updateGynecologyInfo("dateDernierRegle", value)
              }
              type="date"
              icon={Calendar}
            />

            <InputField
              label="Cycle tous les (jours)"
              value={formData.informationGynecologique?.cycleTousLesJours || ""}
              onChange={(value) =>
                updateGynecologyInfo("cycleTousLesJours", value)
              }
              type="number"
              icon={Clock}
            />

            <InputField
              label="Nombre d'enfants"
              value={formData.informationGynecologique?.nombreEnfants || ""}
              onChange={(value) => updateGynecologyInfo("nombreEnfants", value)}
              type="number"
              icon={Baby}
            />

            <InputField
              label="Nombre d'enfants vivants"
              value={
                formData.informationGynecologique?.nombreEnfantsVivants || ""
              }
              onChange={(value) =>
                updateGynecologyInfo("nombreEnfantsVivants", value)
              }
              type="number"
              icon={Baby}
            />

            <InputField
              label="Nombre d'enfants décédés"
              value={
                formData.informationGynecologique?.nombreEnfantsDecedes || ""
              }
              onChange={(value) =>
                updateGynecologyInfo("nombreEnfantsDecedes", value)
              }
              type="number"
              icon={Baby}
            />

            <InputField
              label="Nombre de mort-nés"
              value={formData.informationGynecologique?.nombreMortNes || ""}
              onChange={(value) => updateGynecologyInfo("nombreMortNes", value)}
              type="number"
              icon={Baby}
            />

            <InputField
              label="Nombre de fausses couches"
              value={
                formData.informationGynecologique?.nombreFausseCouches || ""
              }
              onChange={(value) =>
                updateGynecologyInfo("nombreFausseCouches", value)
              }
              type="number"
              icon={Baby}
            />

            <InputField
              label="Type d'accouchement"
              value={formData.informationGynecologique?.typeAccouchement || ""}
              onChange={(value) =>
                updateGynecologyInfo("typeAccouchement", value)
              }
              icon={Baby}
            />
          </div>

          {/* Section Suivi de Grossesse */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <Baby className="mr-2 text-pink-600" size={24} />
                Suivi de Grossesse
              </h3>
              <button
                type="button"
                onClick={togglePregnancyForm}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${showPregnancyForm
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                  }`}
              >
                {showPregnancyForm ? 'Masquer le formulaire' : 'Ajouter un suivi de grossesse'}
              </button>
            </div>

            {showPregnancyForm && (
              <div className="space-y-6 bg-pink-50 p-6 rounded-xl border border-pink-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Date de début de grossesse"
                    value={formData.informationGynecologique?.grossesseActuelle?.dateDebutGrossesse || currentDate}
                    onChange={(value) => updatePregnancyInfo("dateDebutGrossesse", value)}
                    type="date"
                    icon={Calendar}
                  />

                  <InputField
                    label="Date d'accouchement prévue"
                    value={formData.informationGynecologique?.grossesseActuelle?.dateAccouchementPrevu || ""}
                    onChange={(value) => updatePregnancyInfo("dateAccouchementPrevu", value)}
                    type="date"
                    icon={Calendar}
                  />

                  <InputField
                    label="Groupe sanguin"
                    value={formData.informationGynecologique?.grossesseActuelle?.groupeSanguin || ""}
                    onChange={(value) => updatePregnancyInfo("groupeSanguin", value)}
                    placeholder="Ex: A, B, AB, O"
                  />

                  <InputField
                    label="Rhésus"
                    value={formData.informationGynecologique?.grossesseActuelle?.rh || ""}
                    onChange={(value) => updatePregnancyInfo("rh", value)}
                    placeholder="Ex: + ou -"
                  />

                  <InputField
                    label="Nombre de foetus"
                    value={formData.informationGynecologique?.grossesseActuelle?.nombreFoetus || "1"}
                    onChange={(value) => updatePregnancyInfo("nombreFoetus", value)}
                    type="number"
                    inputProps={{
                      min: "1",
                      disabled: !formData.informationGynecologique?.grossesseActuelle?.grossesseMultiple
                    }}
                  />

                  <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <CheckboxField
                      label="Grossesse multiple"
                      checked={formData.informationGynecologique?.grossesseActuelle?.grossesseMultiple || false}
                      onChange={(checked) => {
                        updatePregnancyInfo("grossesseMultiple", checked);
                        if (!checked) {
                          updatePregnancyInfo("nombreFoetus", "1");
                        }
                      }}
                    />

                    <CheckboxField
                      label="Grossesse désirée"
                      checked={formData.informationGynecologique?.grossesseActuelle?.grossesseDesiree || false}
                      onChange={(checked) => updatePregnancyInfo("grossesseDesiree", checked)}
                    />

                    <CheckboxField
                      label="RCI"
                      checked={formData.informationGynecologique?.grossesseActuelle?.rci || false}
                      onChange={(checked) => updatePregnancyInfo("rci", checked)}
                    />

                    <CheckboxField
                      label="Menace d'accouchement prématuré"
                      checked={formData.informationGynecologique?.grossesseActuelle?.menaceAccouchementPremature || false}
                      onChange={(checked) => updatePregnancyInfo("menaceAccouchementPremature", checked)}
                    />

                    <CheckboxField
                      label="Diabète gestationnel"
                      checked={formData.informationGynecologique?.grossesseActuelle?.diabeteGestationnel || false}
                      onChange={(checked) => updatePregnancyInfo("diabeteGestationnel", checked)}
                    />

                    <CheckboxField
                      label="Hypertension artérielle"
                      checked={formData.informationGynecologique?.grossesseActuelle?.hypertensionArterielle || false}
                      onChange={(checked) => updatePregnancyInfo("hypertensionArterielle", checked)}
                    />

                    <CheckboxField
                      label="Pré-éclampsie"
                      checked={formData.informationGynecologique?.grossesseActuelle?.preeclampsie || false}
                      onChange={(checked) => updatePregnancyInfo("preeclampsie", checked)}
                    />
                  </div>

                  <div className="col-span-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Antécédents obstétricaux
                    </label>
                    <textarea
                      value={formData.informationGynecologique?.grossesseActuelle?.antecedentsObstetricaux || ""}
                      onChange={(e) => updatePregnancyInfo("antecedentsObstetricaux", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      rows={3}
                      placeholder="Antécédents obstétricaux..."
                    />
                  </div>

                  <div className="col-span-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Traitement en cours
                    </label>
                    <textarea
                      value={formData.informationGynecologique?.grossesseActuelle?.traitementEnCours || ""}
                      onChange={(e) => updatePregnancyInfo("traitementEnCours", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      rows={2}
                      placeholder="Traitements en cours..."
                    />
                  </div>

                  <div className="col-span-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Allergies
                    </label>
                    <input
                      type="text"
                      value={formData.informationGynecologique?.grossesseActuelle?.allergies || ""}
                      onChange={(e) => updatePregnancyInfo("allergies", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      placeholder="Allergies connues..."
                    />
                  </div>

                  <div className="col-span-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Autres pathologies
                    </label>
                    <textarea
                      value={formData.informationGynecologique?.grossesseActuelle?.autresPathologies || ""}
                      onChange={(e) => updatePregnancyInfo("autresPathologies", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      rows={2}
                      placeholder="Autres pathologies à signaler..."
                    />
                  </div>

                  <div className="col-span-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Commentaires
                    </label>
                    <textarea
                      value={formData.informationGynecologique?.grossesseActuelle?.commentaires || ""}
                      onChange={(e) => updatePregnancyInfo("commentaires", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      rows={3}
                      placeholder="Notes et observations..."
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GynecologyFormStep;
